/**
 * Shared view-models for the three canonical widget kinds.
 *
 * A module widget wires its REAL API response to one of these shapes via a
 * `select` projector; the canonical component owns all rendering, state, and
 * chrome. This keeps every status / work-queue / exception tile visually and
 * behaviourally consistent across the long tail of modules while each module
 * stays honest about where its numbers come from.
 */

/** Semantic emphasis for a value or row — mapped to design tokens, never raw colors. */
export type WidgetTone = 'default' | 'positive' | 'warning' | 'critical' | 'muted';

/** Severity ladder for exception rows. */
export type WidgetSeverity = 'critical' | 'major' | 'minor' | 'info';

// ── status ────────────────────────────────────────────────────────────────

export interface StatusMetric {
  /** Stable key used for the row test-id. */
  key: string;
  /** Short label under the value. */
  label: string;
  /** Pre-formatted display value (the projector owns formatting). */
  value: string;
  /** Semantic emphasis. */
  tone?: WidgetTone;
  /** Optional sub-hint (e.g. a delta or unit). */
  hint?: string;
}

export interface StatusModel {
  /** Optional emphasized primary metric shown large. */
  headline?: StatusMetric;
  /** Secondary KPI grid. */
  metrics: StatusMetric[];
}

// ── work-queue ──────────────────────────────────────────────────────────────

export interface WorkQueueItem {
  id: string;
  /** Main label (e.g. work-order number / requisition number). */
  primary: string;
  /** Right-aligned meta (e.g. ETA, requester). */
  secondary?: string;
  /** Small leading badge (e.g. priority). */
  badge?: string;
  tone?: WidgetTone;
}

export interface WorkQueueModel {
  /** Total pending count (may exceed `items.length`, which is the shown page). */
  total: number;
  /** Noun rendered under the count, e.g. "queued" / "pending". */
  unit?: string;
  /** Top-N rows shown at `medium`+ sizes. */
  items: WorkQueueItem[];
}

// ── exception / risk ─────────────────────────────────────────────────────────

export interface ExceptionItem {
  id: string;
  primary: string;
  secondary?: string;
  severity?: WidgetSeverity;
  /** Right-aligned value label (e.g. blocked amount). */
  valueLabel?: string;
}

export interface ExceptionSeverityCounts {
  critical?: number;
  major?: number;
  minor?: number;
}

export interface ExceptionModel {
  /** Total open exceptions/alerts. */
  total: number;
  /** Noun rendered under the count, e.g. "open exceptions". */
  unit?: string;
  /** Optional per-severity breakdown chips. */
  severityCounts?: ExceptionSeverityCounts;
  /** Top-N rows shown at `medium`+ sizes. */
  items: ExceptionItem[];
}

// ── shared props ─────────────────────────────────────────────────────────────

/** Resource+action pair the canonical component feeds to `usePermission`. */
export interface WidgetPermissionRef {
  resource: string;
  action: string;
}

/** Tailwind token class for a tone — text emphasis only. */
export function toneTextClass(tone: WidgetTone | undefined): string {
  switch (tone) {
    case 'positive':
      return 'text-success';
    case 'warning':
      return 'text-warning';
    case 'critical':
      return 'text-destructive';
    case 'muted':
      return 'text-muted-foreground';
    default:
      return '';
  }
}

/** Tailwind token classes for a small badge/chip of a given tone. */
export function toneBadgeClass(tone: WidgetTone | undefined): string {
  switch (tone) {
    case 'positive':
      return 'bg-success/15 text-success';
    case 'warning':
      return 'bg-warning/15 text-warning';
    case 'critical':
      return 'bg-destructive/15 text-destructive';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/** Map an exception severity to a tone for chip/text styling. */
export function severityTone(severity: WidgetSeverity | undefined): WidgetTone {
  switch (severity) {
    case 'critical':
      return 'critical';
    case 'major':
      return 'warning';
    case 'minor':
      return 'muted';
    default:
      return 'default';
  }
}
